import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pagination, Tab, Tabs, Tooltip } from '@heroui/react';
import { ContentWrapper } from '@/widgets/ContentWrapper';
import {
  AssignmentAssessmentType,
  AssignmentStatus,
  useGetAssignmentsQuery,
} from '@/entities/Assignment';
import { DEFAULT_PAGE_SIZE } from '@/shared/types/common';
import { usePageTitle } from '@/shared/lib/hooks';
import { AssignmentItem } from '../../components/AssignmentItem';
import {
  normalizeAssignmentItem,
  parseAssessmentType,
  parsePage,
  parseStatus,
} from '../../lib/utils';

export const AssignmentsView = () => {
  usePageTitle('Assignments');
  const [searchParams, setSearchParams] = useSearchParams();

  const assessmentType = parseAssessmentType(searchParams.get('assessmentType'));
  const status = parseStatus(searchParams.get('status'));
  const page = parsePage(searchParams.get('page'));

  const handleChangeAssessmentType = (key: React.Key) => {
    const newAssessmentType = key as AssignmentAssessmentType;
    if (newAssessmentType !== assessmentType) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('assessmentType', newAssessmentType);
      newParams.set('page', '1');
      setSearchParams(newParams);
    }
  };

  const handleChangeStatus = (key: React.Key) => {
    const newStatus = key as AssignmentStatus;
    if (newStatus !== status) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('status', newStatus);
      newParams.set('page', '1');
      setSearchParams(newParams);
    }
  };

  const handleChangePage = (newPage: number) => {
    if (newPage !== page) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', String(newPage));
      setSearchParams(newParams);
    }
  };

  const queryParams = useMemo(
    () => ({
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      assessmentType,
      status,
    }),
    [assessmentType, status, page],
  );

  const { data, isLoading, error } = useGetAssignmentsQuery(queryParams);

  return (
    <section className="flex flex-col gap-y-2">
      <Tabs
        variant="light"
        selectedKey={assessmentType}
        onSelectionChange={handleChangeAssessmentType}
        classNames={{
          cursor: 'bg-black',
          tabContent: 'text-grey group-data-[selected=true]:text-white',
        }}
      >
        <Tab
          key="formative"
          title={
            <Tooltip
              content="Weekly or practice task - not graded, but with lecturer feedback"
              placement="bottom-start"
              classNames={{
                content: 'bg-[#211D55] text-white rounded',
                base: 'before:bg-[#211D55]',
              }}
              offset={12}
              showArrow
            >
              Formative
            </Tooltip>
          }
        />
        <Tab
          key="summative"
          title={
            <Tooltip
              content="Graded module assignment checked for quality and plagiarism"
              placement="bottom-start"
              classNames={{
                content: 'bg-[#211D55] text-white rounded',
                base: 'before:bg-[#211D55]',
              }}
              offset={12}
              showArrow
            >
              Summative
            </Tooltip>
          }
        />
      </Tabs>
      <Tabs
        variant="underlined"
        selectedKey={status}
        onSelectionChange={handleChangeStatus}
        classNames={{
          cursor: 'bg-black',
          tabContent: 'text-grey group-data-[selected=true]:text-black',
        }}
      >
        <Tab key="active" title="Active" />
        <Tab key="completed" title="Completed" />
      </Tabs>
      <ContentWrapper
        isLoading={isLoading}
        isError={!!error}
        isEmpty={!data || data.items.length === 0}
        emptyMessage="There are no assignments."
        className="flex flex-col gap-y-6"
      >
        {data?.items?.map((item) => (
          <AssignmentItem
            key={`${item.assignment.id}-${item.lesson.groupLessonId ?? item.lesson.groupExamResubmissionId ?? item.lesson.id}`}
            item={normalizeAssignmentItem({ item, assessmentType, status })}
          />
        ))}
      </ContentWrapper>
      {data?.pagination?.pageCount && data.pagination.pageCount > 1 ? (
        <div className="flex justify-center">
          <Pagination page={page} total={data.pagination.pageCount} onChange={handleChangePage} />
        </div>
      ) : null}
    </section>
  );
};
